import * as NavigationMenu from '@radix-ui/react-navigation-menu'
import { useAppAuth } from '../lib/auth/app-auth'
import { useMutation } from 'convex/react'
import {
  ArrowRight,
  Bookmark,
  Check,
  ChevronDown,
  CircleUserRound,
  Command,
  Heart,
  Mail,
  Menu,
  Moon,
  Play,
  Search,
  Sun,
} from 'lucide-react'
import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { ReactNode } from 'react'
import { api } from '../../convex/_generated/api'
import {
  anonIdCookieName,
  createEmptyAnonymousState,
  getSignupPrompt,
  mediaConsentCookieName,
  parseAnonymousState,
  pendingStateStorageName,
  recordDailyActivity,
  setLessonProgress,
  themeCookieName,
  toggleLikedTarget,
  toggleSavedTarget,
} from '../lib/anonymous-state'
import type { ConsentState, SignupPromptReason } from '../lib/anonymous-state'
import { useCookieConsent } from '../lib/use-cookie-consent'
import type { ConsentSource } from '../lib/use-cookie-consent'
import { utcDateKey } from '../lib/learning-stats'
import {
  MobileAuthCta,
  NavPrimaryCta,
  TopBannerAuthAction,
  useSignedInHint,
} from './auth-cta'
import { getCatalogIndex } from '../content/catalog-index'
import { getCookie, setCookie } from '../lib/cookies'
import {
  LOCALES,
  equivalentPath,
  localizedPath,
  persistLocale,
  useLocale,
  useTranslation,
} from '../lib/i18n'
import {
  buildFooterModel,
  buildMegaMenus,
  buildTopNavLinks,
  buildMobileSections,
} from './nav-config'
import { AnalyticsRuntime } from './analytics-runtime'
import { FoyerWidget } from './FoyerWidget'
import type { MegaMenuDefinition } from './nav-config'
import { ProBadge } from './pro-badge'
import { useRouterState } from '@tanstack/react-router'
import { trackEvent } from '../lib/analytics'
import { NewsletterOptIn } from './newsletter-opt-in'

// Heavy, interaction-only dialog bodies (search palette + mobile menu) are split
// into their own chunk via React.lazy. They are never rendered on first paint -
// the triggers below render eagerly and only mount these once the dialog opens -
// so this keeps @radix-ui/react-dialog, @radix-ui/react-accordion and the search
// index off the initial client bundle without any SSR/hydration risk.
const SearchCommandBody = lazy(() =>
  import('./nav-dialogs').then((m) => ({ default: m.SearchCommandBody })),
)
const MobileMenuBody = lazy(() =>
  import('./nav-dialogs').then((m) => ({ default: m.MobileMenuBody })),
)

export function AppShell({ children }: { children: ReactNode }) {
  const [stickyVisible, setStickyVisible] = useState(false)

  useEffect(() => {
    const hero = document.querySelector('[data-hero-sentinel]')
    const footer = document.querySelector('[data-footer-sentinel]')
    if (!hero || !footer) return

    // The hero sentinel is the hero/header container itself. With threshold 0,
    // isIntersecting stays true as long as any pixel of the hero is in the
    // viewport, so pastHero only flips to true once the entire hero has
    // scrolled out the top. That keeps the sticky nav hidden until the user
    // is genuinely past the hero rather than 1px below it.
    let pastHero = false
    let inFooter = false
    const update = () => setStickyVisible(pastHero && !inFooter)
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.target === hero) pastHero = !entry.isIntersecting
          if (entry.target === footer) inFooter = entry.isIntersecting
        }
        update()
      },
      { threshold: 0 },
    )

    observer.observe(hero)
    observer.observe(footer)
    return () => observer.disconnect()
  }, [])

  return (
    <>
      <TopBanner />
      {/*
        The hero nav is rendered once here - directly under the top banner and
        outside every page's content container - so it is pixel-identical on
        every route. Rendering it per-page (inside `.page-shell`, `.narrow`,
        the home hero, etc.) made its width depend on the page container: a
        `.narrow` page shrank the nav enough to trip its content-overflow
        compact mode, and a container's top padding pushed the nav down off
        the banner seam. Living in the shell, it always spans the full
        `--shell` width and sits flush against the banner.
      */}
      <HeroNav />
      <StickyNav visible={stickyVisible} />
      {/*
        Single shared blur scrim for the mega menu. It lives at the AppShell
        root - outside both navs' stacking contexts - so its backdrop-filter
        applies to the whole page behind it. Visibility is driven by the
        `mega-open` class that the active NavigationBar toggles on <body>.
      */}
      <div className="mega-backdrop" aria-hidden="true" />
      {/*
        Horizontal-overflow containment lives on THIS wrapper, not on <body>.
        The page content holds full-bleed 100vw bands (page-intro/lesson-header
        auras) that would otherwise add a scrollbar-width of horizontal scroll,
        so they must be clipped. Putting `overflow-x: clip` on <body> clipped
        them, but it also made <body> a clip ancestor of the fixed StickyNav -
        which silently breaks its `backdrop-filter` on GPU-composited Chromium
        (Brave), while software-rendered headless tolerated it. Scoping the clip
        to the scrollable content here keeps the fixed nav's ancestor chain free
        of any overflow clip, so the frosted blur composites correctly. The nav,
        sticky nav and footer sit OUTSIDE this wrapper on purpose.
      */}
      <div className="app-content">{children}</div>
      <PreFooter />
      <SiteFooter />
      <CookieBanner />
      {/*
        Analytics runtime (M5): PostHog EU, lazy-loaded after `load` + idle via
        the same-origin `/ingest` proxy. Cookieless (no storage, no replay, no
        profiles) until the visitor opts into "Analytics"; then full
        persistence + masked session replay. The Portfolio Analytics tracker
        is consent-gated by the same runtime (it writes web storage). Consent
        changes apply live. Identification lives in `AnalyticsIdentity`
        (providers.tsx). See docs/ANALYTICS.md.
      */}
      <AnalyticsRuntime />
      {/*
        Foyer support chat widget (bottom-right). Self-contained: it reads the
        current route and loads ONLY on public marketing/content pages, never in
        the private account/admin/auth area. Domain-locked to production, so it
        does not appear on localhost - that is expected.
      */}
      <FoyerWidget />
    </>
  )
}

function TopBanner() {
  const { t } = useTranslation()
  return (
    <div className="top-banner" role="region" aria-label={t('nav.toolbarLabel')}>
      <div className="top-banner-inner">
        {/*
          Right-aligned utility cluster, ordered left -> right:
          [language picker] · [theme toggle] · [sign in / sign out]. The
          cluster's right edge lines up with the nav CTA's right edge below it
          because the banner inner and the nav shell share the same shell width
          + 25px horizontal padding, and the trailing item sits flush right.
          Search lives in the nav bar (left of the CTA), not here. The auth
          action is the shared, optimistic {@link TopBannerAuthAction} so the
          banner never shows "Dashboard" (the nav CTA owns that word) - it shows
          "Sign out" when signed in, so "Dashboard" appears exactly once per bar.
        */}
        <div className="top-banner-actions">
          <LanguagePicker />
          <ThemeToggle />
          <TopBannerAuthAction />
        </div>
      </div>
    </div>
  )
}

/**
 * Inline locale-code picker (e.g. "EN · DE"). Each locale is a plain text link
 * to the equivalent URL of the current page (e.g. /courses <-> /de/kurse); the
 * active locale is rendered as emphasised, non-interactive text. Driven entirely
 * by the shared {@link LOCALES} list, so adding a locale needs no change here.
 */
function LanguagePicker() {
  const active = useLocale()
  const { dict } = useTranslation()
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  })

  function switchLocale(target: (typeof LOCALES)[number]) {
    persistLocale(target)
    window.location.assign(equivalentPath(pathname, target))
  }

  return (
    <span className="language-picker" data-active={active}>
      {LOCALES.map((code, index) => {
        const isActive = code === active
        return (
          <span className="language-picker-item" key={code}>
            {index > 0 ? (
              <span className="language-picker-sep" aria-hidden="true">
                ·
              </span>
            ) : null}
            {isActive ? (
              <span
                className="language-picker-code is-active"
                aria-current="true"
              >
                {code.toUpperCase()}
              </span>
            ) : (
              <button
                className="language-picker-code"
                type="button"
                onClick={() => switchLocale(code)}
              >
                {/* Visible code + full name as the accessible name, without an
                    aria-label that would contradict the visible "IT" (axe
                    label-content-name-mismatch, WCAG 2.5.3). */}
                <span aria-hidden="true">{code.toUpperCase()}</span>
                <span className="sr-only" lang={code}>
                  {dict.nav.languageName[code]}
                </span>
              </button>
            )}
          </span>
        )
      })}
    </span>
  )
}

export function HeroNav() {
  return <NavigationBar variant="hero" />
}

function StickyNav({ visible }: { visible: boolean }) {
  return (
    <div
      className={`sticky-nav ${visible ? 'is-visible' : ''}`}
      aria-hidden={!visible}
      inert={!visible ? true : undefined}
    >
      <NavigationBar variant="sticky" />
    </div>
  )
}

type NavVariant = 'hero' | 'sticky'

// The mega-menu model is derived per locale from the shared route map +
// dictionary + catalog (see {@link ./nav-config.ts}). NavigationBar builds it
// inside its render body from the active locale so language switches and content
// publishes flow through automatically.

// Run layout effects on the client and skip on the server. Avoids React's
// "useLayoutEffect does nothing on the server" warning during SSR while
// still letting us measure the nav synchronously before paint on hydrate.
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect

function NavigationBar({ variant }: { variant: NavVariant }) {
  const { t, locale } = useTranslation()
  const megaMenus = useMemo(() => buildMegaMenus(locale), [locale])
  const topLinks = useMemo(() => buildTopNavLinks(locale), [locale])
  const shellRef = useRef<HTMLElement | null>(null)
  // `null` before the first measurement: the CSS media-query fallback is
  // still in charge. After hydration this flips to `true` (compact) or
  // `false` (expanded) based on actual content overflow.
  const [compact, setCompact] = useState<boolean | null>(null)

  useIsomorphicLayoutEffect(() => {
    const shell = shellRef.current
    if (!shell || typeof ResizeObserver === 'undefined') return

    // Cache of the natural (uncompressed) width the nav requires to fit
    // logo + middle items + actions side-by-side. Re-measured every time
    // we're in expanded mode so font swaps, locale changes, etc. stay
    // accurate.
    let naturalWidth = 0
    let raf = 0

    const measureNatural = () => {
      const brand = shell.querySelector<HTMLElement>('.brand-lockup')
      // `.nav-menu-list` (not `.nav-menu`) is the flex row of triggers.
      // Each trigger has `flex-shrink: 0`, so the list's `scrollWidth`
      // is the true minimum width the middle column needs.
      const list = shell.querySelector<HTMLElement>('.nav-menu-list')
      const actions = shell.querySelector<HTMLElement>('.nav-actions')
      if (!brand || !list || !actions) return 0

      // Force expanded layout for measurement. Both reads happen inside
      // one JS turn, so the browser never paints this intermediate state.
      const compactBefore = shell.classList.contains('is-compact')
      const hydratedBefore = shell.classList.contains('is-hydrated')
      shell.classList.add('is-hydrated')
      shell.classList.remove('is-compact')

      const style = window.getComputedStyle(shell)
      const padX =
        parseFloat(style.paddingLeft || '0') +
        parseFloat(style.paddingRight || '0')
      const colGap =
        parseFloat(style.columnGap || '0') || parseFloat(style.gap || '0') || 0

      // 3-column grid = 2 gaps between brand / middle / actions.
      const total =
        brand.scrollWidth +
        list.scrollWidth +
        actions.scrollWidth +
        padX +
        colGap * 2 +
        24 // breathing room so we switch *before* things visually touch

      if (!hydratedBefore) shell.classList.remove('is-hydrated')
      if (compactBefore) shell.classList.add('is-compact')

      return total
    }

    const evaluate = () => {
      const w = measureNatural()
      if (w > 0) naturalWidth = w
      const next = naturalWidth > 0 && shell.clientWidth < naturalWidth
      setCompact((prev) => (prev === next ? prev : next))
    }

    // Synchronous first measurement inside a layout effect so React paints
    // the correct mode on hydration (no expanded-then-collapse flicker on
    // narrow viewports).
    evaluate()

    const observer = new ResizeObserver(() => {
      // Debounce to one frame to coalesce rapid resize ticks and avoid
      // measuring mid-animation.
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(evaluate)
    })
    observer.observe(shell)

    return () => {
      cancelAnimationFrame(raf)
      observer.disconnect()
    }
  }, [])

  const compactClass = compact === true ? ' is-compact' : ''
  // Once JS has taken over, mark the shell as hydrated so the pre-hydration
  // CSS media-query fallback steps aside.
  const hydratedClass = compact === null ? '' : ' is-hydrated'

  // The featured plate images are only mounted after the user first opens a
  // menu. The mega panels live in the landing DOM, so without this gate the
  // browser would eagerly fetch all five plate images on initial page load and
  // regress landing performance. Flipping this once on first open lets the
  // images mount (then `loading="lazy"` + decoding="async" keep them cheap)
  // while never costing the initial render.
  const [hasOpened, setHasOpened] = useState(false)

  // While any mega menu is open we blur the rest of the page via a fixed
  // backdrop rendered once in AppShell. The open/close signal is a body
  // class so a single backdrop element can serve both the hero and sticky
  // navs without nesting it inside their (transformed / z-indexed) stacking
  // contexts, which would otherwise scope the backdrop-filter to the nav.
  const onMenuValueChange = (value: string) => {
    const open = value !== ''
    if (open) setHasOpened(true)
    if (typeof document !== 'undefined') {
      document.body.classList.toggle('mega-open', open)
    }
  }

  // Safety net: if this nav unmounts while a menu is open, drop the class so
  // the page never stays blurred.
  useEffect(
    () => () => {
      if (typeof document !== 'undefined') {
        document.body.classList.remove('mega-open')
      }
    },
    [],
  )

  return (
    <nav
      ref={shellRef}
      className={`nav-shell nav-shell-${variant}${compactClass}${hydratedClass}`}
      aria-label={t('nav.mainLabel')}
    >
      <a
        href={localizedPath('home', locale)}
        className="brand-lockup"
        aria-label="Agentic School"
      >
        <BrandWordmark />
      </a>

      {/*
        Radix NavigationMenu is the correct primitive for a multi-trigger
        navigation bar with hover-to-open, shared viewport, animated
        transitions between items, single-open-at-a-time, and full
        keyboard a11y (arrow keys, Tab, Escape). Using DropdownMenu for
        this was an architectural mismatch - DropdownMenu is built for
        single, isolated, click-driven menus and brings modal scroll-lock,
        per-instance positioning, and no built-in coordination with
        siblings.
      */}
      <NavigationMenu.Root
        className="nav-menu"
        data-variant={variant}
        onValueChange={onMenuValueChange}
        // Default delays already give a nice grace period when moving
        // between trigger and content; keep Radix's defaults so the UX
        // matches the rest of the platform.
      >
        <NavigationMenu.List className="nav-menu-list">
          {megaMenus.map((menu) => (
            <NavigationMenu.Item key={menu.id} value={menu.id}>
              <NavigationMenu.Trigger
                className="nav-trigger"
                // The default Trigger toggles closed on click. For a
                // hover-driven nav we want clicks to be no-ops so the
                // user can also click the label to navigate to its
                // section root if we wire that up later, and so a
                // mis-click doesn't immediately collapse the menu the
                // user just hovered open.
                onClick={(event) => event.preventDefault()}
              >
                {menu.label}
                <ChevronDown
                  size={14}
                  aria-hidden
                  className="nav-trigger-caret"
                />
              </NavigationMenu.Trigger>
              <NavigationMenu.Content className="mega-menu">
                <MegaMenuBody menu={menu} showImage={hasOpened} />
              </NavigationMenu.Content>
            </NavigationMenu.Item>
          ))}
          {/* Plain top-level links (Pricing): part of the measured list, so
              the burger switch still triggers before anything collides. */}
          {topLinks.map((link) => (
            <NavigationMenu.Item key={link.id} value={link.id}>
              <NavigationMenu.Link asChild>
                <a
                  className="nav-trigger nav-top-link"
                  href={link.href}
                  data-nav-link={link.id}
                  onClick={() =>
                    trackEvent('upsell_cta_clicked', {
                      surface: 'nav_pricing',
                      cta: 'pricing',
                    })
                  }
                >
                  {link.label}
                </a>
              </NavigationMenu.Link>
            </NavigationMenu.Item>
          ))}
        </NavigationMenu.List>

        {/*
          Single shared viewport. Radix portals each Content into this
          element, which lets us position it once (absolute viewport
          centre, just below the nav) and get free width/height
          animations between menus of different sizes via the
          --radix-navigation-menu-viewport-{width,height} CSS vars.
          No transformed ancestor exists, so `position: fixed` on the
          Viewport correctly anchors to the page.
        */}
        <NavigationMenu.Viewport className="nav-menu-viewport" />
      </NavigationMenu.Root>

      <div className="nav-actions">
        {/*
          Search lives in the nav bar, immediately LEFT of the primary CTA
          (not in the top utility bar). It collapses with the rest of the
          desktop nav into the burger in compact mode; the "/" shortcut keeps
          it reachable everywhere.
        */}
        <SearchCommand />
        <NavPrimaryCta location={variant === 'sticky' ? 'nav_sticky' : 'nav_hero'} />
        <MobileMenu />
      </div>
    </nav>
  )
}

function MegaMenuBody({
  menu,
  showImage,
}: {
  menu: MegaMenuDefinition
  showImage: boolean
}) {
  const Icon = menu.icon
  return (
    <div className="mega-grid">
      {/*
        Editorial plate: a branded, theme-aware visual panel (left). It links
        to the section overview, but is hidden from the tab order and screen
        readers (the real, labelled overview link lives in the body) so the
        decorative tile doesn't create a duplicate, unlabelled tab stop.
      */}
      <a
        className="mega-plate"
        href={menu.overviewHref}
        tabIndex={-1}
        aria-hidden="true"
      >
        {/*
          Featured 4:3 plate photo. Mounted only after the menu is first
          opened (`showImage`) so the landing never eagerly fetches it; then
          lazy + async-decoded with explicit dimensions (the plate is a fixed
          4:3 container, so no layout shift). The branded gradient + glyph +
          label sit above it via z-index for a consistent, readable overlay.
        */}
        {showImage ? (
          <img
            className="mega-plate-img"
            src={menu.image}
            alt=""
            aria-hidden="true"
            loading="lazy"
            decoding="async"
            width={1100}
            height={825}
          />
        ) : null}
        <span className="mega-plate-glow" aria-hidden="true" />
        <Icon className="mega-plate-icon" aria-hidden="true" />
        <span className="mega-plate-label">
          <span className="mega-plate-rule" aria-hidden="true" />
          {menu.label}
        </span>
      </a>

      <div className="mega-body">
        <div className="mega-body-head">
          <p className="mega-tagline">{menu.tagline}</p>
          <NavigationMenu.Link asChild>
            <a className="mega-overview" href={menu.overviewHref}>
              {menu.overviewLabel}
              <ArrowRight size={16} aria-hidden="true" />
            </a>
          </NavigationMenu.Link>
        </div>

        <ul className="mega-index">
          {menu.links.map((link, index) => (
            <li className="mega-index-item" key={link.href}>
              <NavigationMenu.Link asChild>
                <a className="mega-index-link" href={link.href}>
                  <span className="mega-index-bar" aria-hidden="true" />
                  <span className="mega-index-num">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="mega-index-label">
                    {link.label}
                    {link.pro ? <ProBadge className="is-inline" /> : null}
                  </span>
                  <ArrowRight
                    className="mega-index-arrow"
                    size={16}
                    aria-hidden="true"
                  />
                </a>
              </NavigationMenu.Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

function BrandWordmark() {
  return (
    <img
      src="/wordmark.webp"
      alt=""
      className="brand-wordmark"
      width={508}
      height={56}
    />
  )
}

function MobileMenu() {
  const { t, locale } = useTranslation()
  const sections = useMemo(() => buildMobileSections(locale), [locale])
  // Optimistic auth: resolved eagerly here (not in the lazy dialog chunk) via
  // the shared hint and passed down so the burger's footer CTA defaults to the
  // signed-out pair and only swaps to "Dashboard" once Clerk has loaded AND
  // confirmed sign-in - the exact same rule the desktop bars use.
  const signedIn = useSignedInHint()
  const [hydrated, setHydrated] = useState(false)
  const [open, setOpen] = useState(false)
  // Once the dialog has been opened at least once we keep the lazy chunk mounted
  // (Radix handles visibility via `open`), so re-opening is instant and Radix's
  // own open/close transition runs normally.
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setHydrated(true)
  }, [])

  function openMenu() {
    setMounted(true)
    setOpen(true)
  }

  return (
    <>
      {/*
        Eager trigger: renders server-side and carries `data-hydrated` for the
        e2e mobile-menu gate. Clicking it mounts the lazy dialog body (which owns
        the Radix Dialog.Root, focus trap and Escape handling) and opens it.
      */}
      <button
        className="icon-button menu-button"
        type="button"
        aria-label={t('nav.openMenu')}
        aria-haspopup="dialog"
        aria-expanded={open}
        data-hydrated={hydrated}
        onClick={openMenu}
      >
        <Menu size={20} />
      </button>
      {mounted ? (
        <Suspense fallback={null}>
          <MobileMenuBody
            open={open}
            onOpenChange={setOpen}
            sections={sections}
            themeToggle={<ThemeToggle />}
            languageToggle={<LanguagePicker />}
            locale={locale}
            authCta={<MobileAuthCta signedIn={signedIn} />}
          />
        </Suspense>
      ) : null}
    </>
  )
}

function ThemeToggle() {
  const { t } = useTranslation()
  const [theme, setTheme] = useState<'dark' | 'light'>('light')

  useEffect(() => {
    const stored = getCookie(themeCookieName) as 'dark' | 'light' | null
    const initial = stored === 'light' || stored === 'dark' ? stored : 'light'
    setTheme(initial)
    document.documentElement.dataset.theme = initial
  }, [])

  function toggle() {
    const next = theme === 'dark' ? 'light' : 'dark'
    setTheme(next)
    document.documentElement.dataset.theme = next
    setCookie(themeCookieName, next, 180)
  }

  return (
    <button
      className="icon-button"
      type="button"
      onClick={toggle}
      aria-label={t('nav.toggleTheme')}
    >
      {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
    </button>
  )
}

export function ActionButton({
  id,
  kind,
  label,
  targetType = 'lesson',
}: {
  id: string
  kind: 'save' | 'like' | 'complete'
  label: string
  targetType?: 'lesson' | 'course' | 'post' | 'resource' | 'changelog'
}) {
  const [active, setActive] = useState(false)
  const [promptReason, setPromptReason] = useState<SignupPromptReason | null>(
    null,
  )
  const [hydrated, setHydrated] = useState(false)
  const { t } = useTranslation()
  const { isSignedIn } = useAppAuth()
  const toggleSaveMutation = useMutation(api.interactions.toggleSave)
  const toggleReactionMutation = useMutation(api.interactions.toggleReaction)
  const updateProgressMutation = useMutation(
    api.interactions.updateLessonProgress,
  )
  const Icon = kind === 'save' ? Bookmark : kind === 'like' ? Heart : Check

  useEffect(() => {
    if (typeof window === 'undefined') return
    const state = readPendingState()
    const progress = (state.progress as Partial<typeof state.progress>)[id]
    if (kind === 'save') setActive(state.saved.lessons.includes(id))
    if (kind === 'like') setActive(state.likes.lessons.includes(id))
    if (kind === 'complete') setActive(Boolean(progress?.completed))
    setHydrated(true)
  }, [id, kind])

  function toggle() {
    const next = !active
    setActive(next)
    if (typeof window !== 'undefined') {
      const current = readPendingState()
      const wasCompleted = Boolean(
        (current.progress as Partial<typeof current.progress>)[id]?.completed,
      )
      let updated =
        kind === 'save'
          ? toggleSavedTarget(current, 'lessons', id)
          : kind === 'like'
            ? toggleLikedTarget(current, 'lessons', id)
            : setLessonProgress(current, id, {
                completed: next,
                watchedSeconds: next ? 1 : 0,
              })
      // Count an anonymous lesson completion into today's local activity only on
      // the transition into completed, mirroring the server-side rule so the
      // streak/stats are consistent across sign-in.
      if (kind === 'complete' && next && !wasCompleted) {
        updated = recordDailyActivity(updated, utcDateKey(Date.now()), {
          lessonsCompleted: 1,
        })
        // Core learning-conversion event: fired once on the transition INTO
        // completed (not on un-complete, not on re-render), so each completion
        // is counted exactly once (consent-aware routing in `trackEvent`).
        trackEvent('lesson_completed', { lesson: id })
      }
      // Saving a lesson is a meaningful engagement/intent signal; fire only on
      // the transition into saved.
      if (kind === 'save' && next) {
        trackEvent('lesson_saved', { lesson: id })
      }
      writePendingState(updated)
      setPromptReason(getSignupPrompt(updated))
    }

    if (isSignedIn && kind === 'save') {
      void toggleSaveMutation({ targetType, targetId: id })
    }
    if (isSignedIn && kind === 'like' && targetType !== 'course') {
      void toggleReactionMutation({ targetType, targetId: id })
    }
    if (isSignedIn && kind === 'complete' && targetType === 'lesson') {
      void updateProgressMutation({
        lessonSlug: id,
        completed: next,
        watchedSeconds: next ? 1 : 0,
      })
    }
  }

  return (
    <span className="state-button-wrap">
      <button
        className={`state-button ${active ? 'is-active' : ''}`}
        type="button"
        onClick={toggle}
        aria-pressed={active}
        data-action-kind={kind}
        data-action-target={id}
        data-hydrated={hydrated}
      >
        <Icon size={16} />
        {label}
      </button>
      {promptReason ? (
        <small className="signup-nudge">
          {t(`signupPrompt.${promptReason}`)}
        </small>
      ) : null}
    </span>
  )
}

export function VideoConsentGate({
  title,
  youtubeId,
}: {
  title: string
  youtubeId: string
}) {
  const { t, locale } = useTranslation()
  const [consent, setConsent] = useState(false)

  useEffect(() => {
    setConsent(getCookie(mediaConsentCookieName) === 'true')
  }, [])

  if (consent) {
    return (
      <iframe
        className="video-frame"
        src={`https://www.youtube-nocookie.com/embed/${youtubeId}`}
        title={title}
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
    )
  }

  return (
    <div className="consent-video">
      <Play size={34} />
      <div>
        <strong>{t('consent.video.title')}</strong>
        <p>{t('consent.video.body')}</p>
      </div>
      <button
        className="primary-button compact"
        type="button"
        onClick={() => {
          setCookie(mediaConsentCookieName, 'true', 180)
          setConsent(true)
        }}
      >
        {t('consent.video.load')}
      </button>
      <a href={localizedPath('cookies', locale)}>
        {t('consent.video.settings')}
      </a>
    </div>
  )
}

/**
 * Inline, underlined text action used for the banner's secondary choices
 * ("Necessary only", "Customize") so the compact banner has exactly ONE primary
 * button ("Accept all"). Rendered as a real `<button>` so it is keyboard
 * operable and announced as an action. Muted by default, resolves to the full
 * foreground colour on hover/focus.
 */
function CookieInlineAction({
  children,
  onClick,
}: {
  children: ReactNode
  onClick: () => void
}) {
  return (
    <button type="button" className="cookie-link" onClick={onClick}>
      {children}
    </button>
  )
}

/**
 * GDPR / Swiss FADP / CCPA-compliant cookie banner. Full-width bottom bar with
 * its inner content constrained to the exact same shell container the top nav
 * uses, so its left/right edges line up with the nav logo and CTA. Fully opaque
 * surface in both themes (a consent surface must never reveal what it gates).
 *
 * One primary "Accept all" CTA; everything else (one-click "Necessary only",
 * the granular "Customize" panel, and the cookie-policy link) is an inline muted
 * text link. Consent persistence, the GPC auto opt-out, and the reopen path all
 * live in {@link useCookieConsent}; this component is purely presentational on
 * top of it. When authenticated, each decision is also recorded server-side via
 * the `recordConsent` Convex mutation for an auditable consent log.
 */
function CookieBanner() {
  const { t, locale } = useTranslation()
  const recordConsent = useMutation(api.consent.recordConsent)

  // Mirror every persisted decision to the server consent log (best-effort:
  // server sync must never block or break the client-side consent experience).
  const onPersist = useCallback(
    (next: ConsentState, source: ConsentSource) => {
      void recordConsent({
        anonId: getCookie(anonIdCookieName) ?? undefined,
        consentVersion: next.version,
        analytics: next.analytics,
        externalMedia: next.externalMedia,
        marketing: next.marketing,
        source: source === 'gpc' ? 'migration' : source,
      }).catch(() => {})
    },
    [recordConsent],
  )

  const {
    consent,
    showBanner,
    settingsOpen,
    setSettingsOpen,
    acceptAll,
    rejectAll,
    saveCustom,
  } = useCookieConsent(onPersist)

  // Draft for the customization panel. Seeded from the live consent each time
  // the panel opens (inline "Customize" OR the global `open-cookie-settings`
  // event from the footer / cookies page), so it always reflects the saved
  // choices. Without re-seeding, a reopened panel showed granted categories
  // unchecked and "Save" silently withdrew them.
  const [draft, setDraft] = useState<ConsentState>(consent)
  const [seededOpen, setSeededOpen] = useState(settingsOpen)
  if (settingsOpen !== seededOpen) {
    setSeededOpen(settingsOpen)
    if (settingsOpen) setDraft(consent)
  }

  function openCustomize() {
    setDraft(consent)
    setSettingsOpen(true)
  }

  if (!showBanner) return null

  return (
    <div
      className="cookie-banner"
      role="dialog"
      aria-modal="false"
      aria-label={t('consent.banner.regionLabel')}
    >
      <div className="cookie-banner-inner">
        {!settingsOpen ? (
          <div className="cookie-bar">
            {/* One flowing block (~2 lines on desktop): a bold title lead-in,
                the description, then the secondary actions (Necessary only ·
                Customize · Cookie policy) running INLINE right after the
                sentence, middot-separated - nothing sits on its own dedicated
                row, so the bar stays compact. The single dominant "Accept all"
                CTA is the only button. */}
            <p className="cookie-copy">
              <strong>{t('consent.banner.title')}.</strong>{' '}
              {t('consent.banner.body')}{' '}
              <span className="cookie-dot" aria-hidden="true">
                &middot;
              </span>{' '}
              <CookieInlineAction onClick={rejectAll}>
                {t('consent.banner.onlyNecessary')}
              </CookieInlineAction>{' '}
              <span className="cookie-dot" aria-hidden="true">
                &middot;
              </span>{' '}
              <CookieInlineAction onClick={openCustomize}>
                {t('consent.banner.customize')}
              </CookieInlineAction>{' '}
              <span className="cookie-dot" aria-hidden="true">
                &middot;
              </span>{' '}
              <a className="cookie-link" href={localizedPath('cookies', locale)}>
                {t('consent.banner.policyLink')}
              </a>
            </p>
            <div className="cookie-cta">
              <button
                className="cookie-accept primary-button"
                type="button"
                onClick={acceptAll}
              >
                {t('consent.banner.acceptAll')}
              </button>
            </div>
          </div>
        ) : (
          <div className="cookie-customize">
            <div className="cookie-customize-copy">
              <strong>{t('consent.banner.customizeTitle')}</strong>
              <p>{t('consent.banner.body')}</p>
            </div>
            <div className="cookie-options">
              <ConsentToggle
                label={t('consent.banner.essential')}
                description={t('consent.banner.essentialHint')}
                checked
                disabled
                onChange={() => {}}
              />
              <ConsentToggle
                label={t('consent.banner.analytics')}
                description={t('consent.banner.analyticsHint')}
                checked={draft.analytics}
                onChange={(analytics) => setDraft({ ...draft, analytics })}
              />
              <ConsentToggle
                label={t('consent.banner.externalMedia')}
                checked={draft.externalMedia}
                onChange={(externalMedia) =>
                  setDraft({ ...draft, externalMedia })
                }
              />
              <ConsentToggle
                label={t('consent.banner.marketing')}
                checked={draft.marketing}
                onChange={(marketing) => setDraft({ ...draft, marketing })}
              />
            </div>
            <div className="cookie-customize-actions">
              <CookieInlineAction onClick={() => setSettingsOpen(false)}>
                {t('consent.banner.back')}
              </CookieInlineAction>
              <button
                className="primary-button compact"
                type="button"
                onClick={() => saveCustom(draft)}
              >
                {t('consent.banner.savePreferences')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function ConsentToggle({
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  label: string
  description?: string
  checked: boolean
  disabled?: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <label className={`consent-toggle ${disabled ? 'is-locked' : ''}`}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="consent-toggle-text">
        <span className="consent-toggle-label">{label}</span>
        {description ? (
          <span className="consent-toggle-hint">{description}</span>
        ) : null}
      </span>
    </label>
  )
}

/**
 * The two conversion sections that sit above the footer on every page:
 * the "Nächster Schritt" call-to-action and the weekly-newsletter card.
 * Rendered once in {@link AppShell} so the whole site shares one consistent
 * pre-footer instead of each page re-implementing (or omitting) it.
 */
function PreFooter() {
  const locale = useLocale()
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  // Tool detail pages carry their own contextual opt-in (ToolLeadCapture),
  // so the generic panel would be a second, redundant form.
  const toolsPrefix = `${localizedPath('tools', locale).replace(/\/+$/u, '')}/`
  const onToolPage = pathname.startsWith(toolsPrefix) && pathname.length > toolsPrefix.length
  return (
    <>
      <FinalCta />
      {onToolPage ? null : <NewsletterPanel />}
    </>
  )
}

function FinalCta() {
  const { t, locale } = useTranslation()
  return (
    <section
      className="final-cta-section spotlight-surface"
      aria-label={t('finalCta.title')}
    >
      <div className="final-cta-copy">
        <span className="eyebrow">{t('finalCta.eyebrow')}</span>
        <h2>{t('finalCta.title')}</h2>
        <p>{t('finalCta.body')}</p>
        <div className="final-cta-actions">
          <a
            className="primary-button"
            href={localizedPath('register', locale)}
            data-cta="final_cta"
            data-cta-kind="signup"
          >
            {t('common.signUpCta')}
            <ArrowRight size={18} />
          </a>
          <a
            className="ghost-button"
            href={localizedPath('resources', locale)}
            data-cta="final_cta"
            data-cta-kind="resources"
          >
            {t('finalCta.secondary')}
          </a>
        </div>
      </div>
    </section>
  )
}

export function FormStatus({
  status,
  success,
}: {
  status: 'idle' | 'saving' | 'success' | 'error'
  success: string
}) {
  const { t } = useTranslation()
  if (status === 'idle' || status === 'saving') return null
  return (
    <p
      className={`form-status ${status === 'success' ? 'is-success' : 'is-error'}`}
      role="status"
    >
      {status === 'success' ? success : t('forms.genericError')}
    </p>
  )
}

/**
 * Consent-record + analytics source of the pre-footer newsletter panel: the
 * dedicated newsletter page, the homepage, or "pre_footer" anywhere else.
 */
function useNewsletterSource(): string {
  const locale = useLocale()
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  })
  const clean = pathname.replace(/\/+$/u, '') || '/'
  if (clean === localizedPath('newsletter', locale)) return 'newsletter_page'
  if (clean === (localizedPath('home', locale).replace(/\/+$/u, '') || '/')) {
    return 'home'
  }
  return 'pre_footer'
}

function NewsletterPanel() {
  const { t, dict } = useTranslation()
  // The pre-footer panel sits on every page; tag the source by page type so
  // the consent record and analytics show where people subscribe.
  const source = useNewsletterSource()
  return (
    <section
      className="home-newsletter-section"
      aria-label={dict.newsletter.eyebrow}
    >
      <div className="newsletter-card">
        <div className="newsletter-card-glow" aria-hidden="true" />
        <div className="newsletter-card-grid">
          <div className="newsletter-copy">
            <span className="eyebrow newsletter-eyebrow">
              <Mail size={12} aria-hidden="true" /> {t('newsletter.eyebrow')}
            </span>
            <h2>{t('newsletter.title')}</h2>
            <p>{t('newsletter.body')}</p>
            <ul className="newsletter-perks">
              {dict.newsletter.perks.map((perk) => (
                <li key={perk}>
                  <span className="newsletter-perk-icon" aria-hidden="true">
                    <Check size={14} strokeWidth={3} />
                  </span>
                  <span>{perk}</span>
                </li>
              ))}
            </ul>
          </div>
          <NewsletterOptIn source={source} />
        </div>
      </div>
    </section>
  )
}

function SiteFooter() {
  const { locale } = useTranslation()
  const model = useMemo(() => buildFooterModel(locale), [locale])

  return (
    <footer className="site-footer" data-footer-sentinel>
      <div className="footer-grid">
        <div className="footer-brand">
          <a
            href={localizedPath('home', locale)}
            className="brand-lockup"
            aria-label="Agentic School"
          >
            <BrandWordmark />
          </a>
          <p>{model.tagline}</p>
          <div className="footer-social" aria-label={model.followUs}>
            {/* Glyphs are decorative; the name comes from sr-only text, so
                the visible label never contradicts the accessible name
                (axe label-content-name-mismatch). */}
            {FOOTER_SOCIAL.map((social) => (
              <a key={social.name} href={social.href}>
                <span aria-hidden="true">{social.glyph}</span>
                <span className="sr-only">{social.name}</span>
              </a>
            ))}
          </div>
        </div>
        {model.columns.map((column) => (
          <FooterColumn
            key={column.title}
            title={column.title}
            links={column.links}
          />
        ))}
      </div>
      <div className="footer-bottom">
        <div className="footer-bottom-left">
          <small>© {new Date().getFullYear()} Agentic School</small>
          <span className="swiss-made">
            <svg
              className="swiss-cross"
              viewBox="0 0 32 32"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
              focusable="false"
            >
              <rect width="32" height="32" fill="#e11d2e" />
              <rect x="6" y="13" width="20" height="6" fill="#fff" />
              <rect x="13" y="6" width="6" height="20" fill="#fff" />
            </svg>
            <span>{model.madeIn}</span>
          </span>
          <span className="footer-credit">
            {model.builtBy.prefix}{' '}
            <a
              href={model.builtBy.href}
              target="_blank"
              rel="noopener"
            >
              {model.builtBy.name}
            </a>
          </span>
        </div>
        <ul className="footer-legal" aria-label={model.legalLabel}>
          {model.legalLinks.map((link) => (
            <li key={link.href}>
              <a href={link.href}>{link.label}</a>
            </li>
          ))}
        </ul>
      </div>
    </footer>
  )
}

const FOOTER_SOCIAL = [
  { name: 'LinkedIn', href: 'https://www.linkedin.com', glyph: 'in' },
  { name: 'YouTube', href: 'https://www.youtube.com', glyph: '▶' },
  { name: 'X', href: 'https://x.com', glyph: '𝕏' },
  { name: 'GitHub', href: 'https://github.com', glyph: '{ }' },
] as const

function FooterColumn({
  title,
  links,
}: {
  title: string
  links: Array<{ href: string; label: string }>
}) {
  const locale = useLocale()
  const pricingHref = localizedPath('pricing', locale)
  return (
    <div className="footer-column">
      <strong>{title}</strong>
      {links.map((link) => (
        <a
          key={link.href}
          href={link.href}
          onClick={
            link.href === pricingHref
              ? () =>
                  trackEvent('upsell_cta_clicked', {
                    surface: 'footer_pricing',
                    cta: 'pricing',
                  })
              : undefined
          }
        >
          {link.label}
        </a>
      ))}
    </div>
  )
}

function SearchCommand() {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [hydrated, setHydrated] = useState(false)
  // Stays false until the palette is first opened, which is what triggers the
  // lazy chunk import. After that the body stays mounted so re-opens are instant.
  const [mounted, setMounted] = useState(false)
  const triggerRef = useRef<HTMLButtonElement | null>(null)

  function openSearch() {
    setMounted(true)
    setOpen(true)
  }

  useEffect(() => {
    setHydrated(true)

    function onKeyDown(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null
      const isTyping =
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.isContentEditable
      // Both the hero nav and the sticky nav mount a SearchCommand, so two
      // listeners see every "/". Exactly ONE palette may open: two modal
      // dialogs would each aria-hide the other and leave no usable dialog.
      // The first live (non-inert) instance claims the key via preventDefault.
      if (event.defaultPrevented) return
      if (triggerRef.current?.closest('[inert]')) return
      if ((event.key === '/' || event.code === 'Slash') && !isTyping) {
        event.preventDefault()
        openSearch()
      }
    }

    // The "/" shortcut listener is registered eagerly (cheap) so search opens
    // instantly via keyboard even before the dialog chunk has loaded; opening it
    // is what kicks off the import.
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  return (
    <>
      {/*
        Eager trigger: renders server-side, carries `data-hydrated` for the
        search e2e gate, and opening it (click or "/") mounts the lazy palette
        body that owns the Radix Dialog.
      */}
      <button
        ref={triggerRef}
        className="icon-button nav-search"
        type="button"
        aria-label={t('nav.search')}
        aria-haspopup="dialog"
        aria-expanded={open}
        data-hydrated={hydrated}
        onClick={openSearch}
      >
        <Search size={18} />
      </button>
      {mounted ? (
        <Suspense fallback={null}>
          <SearchCommandBody open={open} onOpenChange={setOpen} />
        </Suspense>
      ) : null}
    </>
  )
}

function readPendingState() {
  try {
    return parseAnonymousState(
      JSON.parse(window.localStorage.getItem(pendingStateStorageName) ?? '{}'),
    )
  } catch {
    return createEmptyAnonymousState()
  }
}

function writePendingState(
  state: ReturnType<typeof createEmptyAnonymousState>,
) {
  const anonId = getCookie(anonIdCookieName) ?? crypto.randomUUID()
  if (!getCookie(anonIdCookieName)) setCookie(anonIdCookieName, anonId, 180)
  window.localStorage.setItem(pendingStateStorageName, JSON.stringify(state))
}

export function SearchEmptyState() {
  const { t, locale } = useTranslation()
  const suggestions = useMemo(() => getCatalogIndex(locale).courses.slice(0, 3), [
    locale,
  ])

  return (
    <div className="search-empty">
      <Command size={28} />
      <p>{t('search.emptyPrompt')}</p>
      <div>
        {suggestions.map((course) => (
          <a
            key={course.slug}
            href={localizedPath('course', locale, { courseSlug: course.slug })}
          >
            {course.title}
          </a>
        ))}
      </div>
    </div>
  )
}

export function ProfileFallback() {
  const { t } = useTranslation()
  return (
    <div className="profile-fallback">
      <CircleUserRound size={22} />
      <span>{t('common.account')}</span>
    </div>
  )
}

